import { useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';

// Increase throttle to prevent excessive auth checks
const MIN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes

export default function AuthHandler() {
  const { setUser, setSession, setLoading } = useAuth();
  const lastCheck = useRef(0);
  const isInitialMount = useRef(true);
  const authChangeSubscription = useRef(null);

  // Separate function to update last login - this should always run
  async function updateLastLogin(userId) {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/last-login`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });
      
      if (response.ok) {
        console.log('[AuthHandler] Updated last login timestamp for user:', userId);
      } else {
        const errorText = await response.text();
        console.error('[AuthHandler] Failed to update last login, server response:', errorText);
      }
    } catch (loginError) {
      console.error('[AuthHandler] Failed to update last login:', loginError);
      // Don't throw - this is not critical for authentication
    }
  }

  async function init(forceRefresh = false) {
    console.log('[AuthHandler] init called. Current session:', await supabase.auth.getSession());
    const now = Date.now();
    if (!forceRefresh && now - lastCheck.current < MIN_REFRESH_INTERVAL) {
      console.log('[AuthHandler] Skipping unnecessary refresh.');
      return;
    }

    lastCheck.current = now;
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setUser(null);
        setSession(null);
        setLoading(false);
        return;
      }

      setUser(session.user);
      setSession(session);

      // Check for user profile
      let { data: profile } = await supabase
        .from('user_profiles')
        .select('role, status')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (profile?.role) {
        setUser(prev => ({ ...prev, role: profile.role }));
      } else {
        // Check invitation
        const { data: invitation } = await supabase
          .from('user_invitations')
          .select('role, status, invited_at')
          .eq('email', session.user.email.toLowerCase())
          .in('status', ['accepted', 'pending'])
          .single();

        if (invitation?.role) {
          setUser(prev => ({ ...prev, role: invitation.role }));

          // Secure RPC call to create profile
          const { error: rpcError } = await supabase.rpc('create_user_profile', {
            user_id: session.user.id,
            user_email: session.user.email.toLowerCase(),
            user_role: invitation.role,
            user_status: 'active',
            user_invited_at: invitation.invited_at || new Date().toISOString(),
            user_activated_at: new Date().toISOString()
          });

          if (rpcError) {
            console.error('[AuthHandler] RPC Error:', rpcError);
          }

          // Migrate pending contract assignments FIRST
          console.log('[AuthHandler] 🔄 Starting contract migration...');
          console.log('[AuthHandler] Migration params:', {
            user_email: session.user.email.toLowerCase(),
            user_id: session.user.id
          });
          
          const { data: migrationResult, error: migrationError } = await supabase.rpc('migrate_pending_contract_assignments', {
            p_user_email: session.user.email.toLowerCase(),
            p_user_id: session.user.id
          });

          if (migrationError) {
            console.error('[AuthHandler] ❌ Migration RPC error:', migrationError);
            console.log('[AuthHandler] 🔄 Trying manual migration fallback...');
            
            try {
              // Fallback: Manual migration
              // 1. Get pending assignments for this email
              const { data: pendingAssignments, error: fetchError } = await supabase
                .from('pending_contract_assignments')
                .select('invitation_id, contract_id')
                .eq('email', session.user.email.toLowerCase());

              if (fetchError) {
                console.error('[AuthHandler] ❌ Error fetching pending assignments:', fetchError);
              } else if (pendingAssignments && pendingAssignments.length > 0) {
                console.log(`[AuthHandler] Found ${pendingAssignments.length} pending assignments`);
                
                // 2. Insert into user_contract_access
                const contractAssignments = pendingAssignments.map(pa => ({
                  user_id: session.user.id,
                  contract_id: pa.contract_id
                }));

                const { data: insertedAssignments, error: insertError } = await supabase
                  .from('user_contract_access')
                  .upsert(contractAssignments, { onConflict: 'user_id,contract_id' })
                  .select();

                if (insertError) {
                  console.error('[AuthHandler] ❌ Error inserting contract assignments:', insertError);
                } else {
                  console.log(`[AuthHandler] ✅ Manually inserted ${insertedAssignments?.length || 0} contract assignments`);
                  
                  // 3. Delete pending assignments
                  const { error: deleteError } = await supabase
                    .from('pending_contract_assignments')
                    .delete()
                    .eq('email', session.user.email.toLowerCase());

                  if (deleteError) {
                    console.error('[AuthHandler] ❌ Error deleting pending assignments:', deleteError);
                  } else {
                    console.log('[AuthHandler] ✅ Manual migration completed successfully!');
                    console.log(`[AuthHandler] Migrated ${insertedAssignments?.length || 0} contracts`);
                  }
                }
              } else {
                console.log('[AuthHandler] No pending assignments found for manual migration');
              }
            } catch (manualError) {
              console.error('[AuthHandler] ❌ Manual migration failed:', manualError);
            }
          } else {
            console.log('[AuthHandler] ✅ Contract migration completed successfully!');
            console.log('[AuthHandler] Migration result:', migrationResult);
            console.log('[AuthHandler] Contracts migrated count:', migrationResult);
          }

          // Delete invitation record AFTER migration
          if (invitation.status === 'pending') {
            const { error: deleteError } = await supabase
              .from('user_invitations')
              .delete()
              .eq('email', session.user.email.toLowerCase());

            if (deleteError) {
              console.error('[AuthHandler] Error deleting invitation record:', deleteError);
            } else {
              console.log('[AuthHandler] ✅ Invitation record deleted after successful signup');
            }
          }

          // Small delay to ensure all database operations complete
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          setUser(prev => ({ ...prev, role: null }));
        }
      }
    } catch (error) {
      console.error('[AuthHandler] General Error:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      init(true);
    }

    // Listen for auth state changes
    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthHandler] Auth state changed:', event);
      
      if (event === 'SIGNED_OUT') {
        init(true);
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Only update last login, don't refresh the entire init to avoid loops
        await updateLastLogin(session.user.id);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Only update last login on token refresh, don't call init
        await updateLastLogin(session.user.id);
      }
      // Explicitly ignore other events to avoid refresh loops
    });

    authChangeSubscription.current = data?.subscription;

    return () => {
      authChangeSubscription.current?.unsubscribe();
    };
  }, []);

  return null;
}
